import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';

/*
 * Invite another platform operator (3 October 2026). Before this a second operator needed an edit of
 * the server's .env. The invitee gets a 7-day link to set a password here, then must set up two-factor
 * sign-in before the first session.
 */
export function InviteStaff({ icon }: { icon?: React.ReactNode }) {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => admin.inviteStaff({ email: email.trim(), display_name: name.trim() || undefined }),
    onSuccess: () => {
      notifySuccess(t('roles.invite.sent', { email: email.trim() }), t('roles.invite.title'));
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      close();
    },
    onError: (err) => setError(
      isApiError(err) && err.code === 'ALREADY_STAFF' ? t('roles.invite.alreadyStaff')
      : isApiError(err) && err.code === 'EMAIL_IN_USE' ? t('roles.invite.emailInUse')
      : isApiError(err) && err.code === 'INVALID_EMAIL' ? t('roles.invite.invalidEmail')
      : (err as Error).message)
  });
  function close() { setOpen(false); setEmail(''); setName(''); setError(null); }
  function submit(e: FormEvent) { e.preventDefault(); setError(null); invite.mutate(); }

  return (
    <>
      <Button size="sm" leftSection={icon} onClick={() => setOpen(true)} data-testid="invite-staff">{t('roles.actions.invite')}</Button>
      <Modal opened={open} onClose={close} title={t('roles.invite.title')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <Text role="bodySmall">{t('roles.invite.intro')}</Text>
            <TextInput label={t('roles.invite.email')} type="email" required value={email}
              onChange={(e) => setEmail(e.currentTarget.value)} error={error ?? undefined} data-testid="invite-staff-email" />
            <TextInput label={t('roles.invite.name')} description={t('roles.invite.nameHint')} value={name}
              onChange={(e) => setName(e.currentTarget.value)} />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={close}>{t('action.cancel')}</Button>
              <Button type="submit" loading={invite.isPending} disabled={!email.trim()} data-testid="invite-staff-send">{t('roles.invite.send')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
